import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHeart, FiUsers, FiTarget, FiTrendingUp } = FiIcons;

const CrowdfundingHero = () => {
  const [raised, setRaised] = useState(2847650);
  const [donors, setDonors] = useState(1247);
  const target = 5000000;

  const progress = (raised / target) * 100;
  const daysLeft = 45;

  // Live ticker effect for donations
  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() > 0.7) {
        const increment = Math.floor(Math.random() * 2000) + 500;
        setRaised(prev => Math.min(prev + increment, target));
        setDonors(prev => prev + 1);
      }
    }, 8000);

    return () => clearInterval(interval);
  }, [target]);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-gray-900 via-orange-900 to-red-900">
      {/* Background Video/Image */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-black/60 z-10"></div>
        <img
          src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
          alt="Transformation story"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
          {/* Main Content - 2/3 width */}
          <div className="lg:col-span-2 text-white">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center space-x-2 mb-6">
                <div className="px-4 py-2 bg-red-600 rounded-full text-sm font-bold">
                  🚨 URGENT CAMPAIGN
                </div>
                <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium">
                  {daysLeft} Days Left
                </div>
              </div>

              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6">
                From Pavement to
                <span className="block text-yellow-400">Possibility</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
                Help us create a <strong className="text-white">Begging-Free India</strong> where every person lives with dignity, not dependency.
              </p>

              <div className="bg-orange-600 rounded-2xl p-6 mb-8 border-2 border-yellow-400">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-semibold">Your Impact</span>
                  <SafeIcon icon={FiHeart} className="w-6 h-6 text-yellow-300" />
                </div>
                <div className="text-3xl font-bold mb-2">₹500 = One Day of Dignity</div>
                <div className="text-orange-100">Shelter + Meals + Skills Training</div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(255, 193, 7, 0.4)' }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-yellow-500 text-gray-900 px-10 py-4 rounded-full text-xl font-bold hover:bg-yellow-400 transition-all duration-300 shadow-2xl"
                >
                  🚀 Donate Now - Save Lives
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/20 transition-all duration-300"
                >
                  📺 Watch Their Stories
                </motion.button>
              </div>
            </motion.div>
          </div>

          {/* Live Fundraising Panel - 1/3 width */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="bg-white rounded-2xl p-6 shadow-2xl"
          >
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Campaign Progress</h3>
              <div className="text-sm text-gray-600">Live Updates Every Few Minutes</div>
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Raised</span>
                <span>{progress.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 2, ease: "easeOut" }}
                />
              </div>
            </div>

            {/* Stats */}
            <div className="space-y-4 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Raised</span>
                <span className="text-2xl font-bold text-green-600">₹{raised.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Goal</span>
                <span className="text-lg font-semibold text-gray-900">₹{target.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Donors</span>
                <span className="text-lg font-bold text-orange-600">{donors.toLocaleString()}</span>
              </div>
            </div>

            {/* Live Donor Ticker */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                Recent Donations
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-700">Anonymous</span>
                  <span className="font-semibold text-green-600">₹2,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Priya S.</span>
                  <span className="font-semibold text-green-600">₹5,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Raj Kumar</span>
                  <span className="font-semibold text-green-600">₹1,500</span>
                </div>
              </div>
            </div>

            {/* Quick Donation Buttons */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <button className="bg-orange-100 text-orange-700 py-3 rounded-lg font-semibold hover:bg-orange-200 transition-colors">
                  ₹500
                </button>
                <button className="bg-orange-100 text-orange-700 py-3 rounded-lg font-semibold hover:bg-orange-200 transition-colors">
                  ₹1,000
                </button>
                <button className="bg-orange-100 text-orange-700 py-3 rounded-lg font-semibold hover:bg-orange-200 transition-colors">
                  ₹5,000
                </button>
                <button className="bg-orange-100 text-orange-700 py-3 rounded-lg font-semibold hover:bg-orange-200 transition-colors">
                  ₹10,000
                </button>
              </div>
              <button className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white py-4 rounded-lg font-bold text-lg hover:from-orange-700 hover:to-red-700 transition-all duration-300 shadow-lg">
                💝 Donate Custom Amount
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-center space-x-4 text-xs text-gray-600">
                <div className="flex items-center">
                  <SafeIcon icon={FiTarget} className="w-4 h-4 mr-1 text-green-500" />
                  80G Certified
                </div>
                <div className="flex items-center">
                  <SafeIcon icon={FiUsers} className="w-4 h-4 mr-1 text-blue-500" />
                  Secure Payment
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Floating Stats */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16"
        >
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center text-white">
            <div className="text-3xl font-bold mb-2">4 Lakh</div>
            <div className="text-gray-300">People Begging in India</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center text-white">
            <div className="text-3xl font-bold mb-2">80%</div>
            <div className="text-gray-300">Willing to Work if Given a Chance</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center text-white">
            <div className="text-3xl font-bold mb-2">2,547+</div>
            <div className="text-gray-300">Lives Already Transformed</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CrowdfundingHero;